import { useTranslation } from 'react-i18next';
import { Accordion } from '../common';
import { useCalculationStore } from '../../stores/calculationStore';
import { useProductionStore } from '../../stores/productionStore';
import { useCurrency } from '../../lib/utils/currency';
import { useUnitStore, KG_TO_LB } from '../../stores/unitStore';

export function VATCalculations() {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { weightUnit } = useUnitStore();
  const taxes = useCalculationStore((state) => state.taxes);
  const metrics = useCalculationStore((state) => state.metrics);

  if (!taxes || !metrics) {
    return null;
  }

  // Calculate average weight from production data
  const productionStore = useProductionStore.getState();
  const harvestEvents = productionStore.harvestEvents;
  const totalCount = harvestEvents.reduce((sum, event) => sum + event.count, 0);
  const totalWeight = harvestEvents.reduce((sum, event) => sum + (event.totalWeight || 0), 0);
  const averageWeight = totalCount > 0 ? totalWeight / totalCount : 0;

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const revenuePerBird = averageWeight * taxes.pricePerKgWithVAT;

  return (
    <Accordion 
      title={
        <div className="flex flex-col items-center justify-center w-full text-center">
          <span className="text-sm mb-1">{t('results.vatCalculations')}</span>
          <span className="text-xl font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">
            {formatCurrency(taxes.vatInclusiveTotal)} {symbol}
          </span>
        </div>
      }
      defaultOpen={false}
    >
      <div className="space-y-3">
        {/* VAT Amount */}
        <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('results.vatRate')}
          </span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {formatCurrency(taxes.vatAmount)} {symbol}
          </span>
        </div>

        {/* VAT Inclusive Total */}
        <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('results.vatInclusiveRevenue')}
          </span>
          <span className="text-base font-bold text-blue-600 dark:text-blue-400">
            {formatCurrency(taxes.vatInclusiveTotal)} {symbol}
          </span>
        </div>

        {/* Price per kg */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
              {t('results.pricePerKgVATExcl')}
            </div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrency(taxes.pricePerKg)} {symbol}/{weightUnit}
            </div>
          </div>

          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
              {t('results.pricePerKgVATIncl')}
            </div>
            <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
              {formatCurrency(taxes.pricePerKgWithVAT)} {symbol}/{weightUnit}
            </div>
          </div>
        </div>

        {/* Revenue per bird */}
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('results.revenuePerBirdVATIncl')}
            </span>
            <span className="text-base font-semibold text-green-600 dark:text-green-400">
              {formatCurrency(revenuePerBird)} {symbol}
            </span>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            {(weightUnit === 'lb' ? (averageWeight || 0) * KG_TO_LB : (averageWeight || 0)).toFixed(3)} {weightUnit} × {formatCurrency(taxes.pricePerKgWithVAT)} {symbol}/{weightUnit}
          </div>
        </div>
      </div>
    </Accordion>
  );
}
